import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { API_BASE_URL } from '../lib/api';
import { getTenantSlug, setTenantSlug, withTenant } from '../lib/tenantHeaders';

const AuthContext = createContext(null);
const STORAGE_USER = 'user';
const STORAGE_TENANT = 'tenant';
const STORAGE_TOKEN = 'token';

export function AuthProvider({ children }) {
  const slug = getTenantSlug();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_USER)) || null;
    } catch {
      return null;
    }
  });
  const [tenant, setTenant] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_TENANT)) || null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_TOKEN) || null);
  const [loading, setLoading] = useState(false);

  // Detección de modo: plataforma vs. tenant
  const isPlatformMode = !slug;
  const isTenantMode = Boolean(slug);

  const logout = useCallback(() => {
    setUser(null);
    setTenant(null);
    setToken(null);
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_TENANT);
    localStorage.removeItem(STORAGE_TOKEN);
    setTenantSlug(null);
  }, []);

  const login = useCallback(async (correo, contrasena) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        // withTenant: imprescindible en prod — el backend ya no puede
        // deducir el tenant del Host porque el front llama a otra URL.
        headers: withTenant({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ correo, contrasena }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Credenciales inválidas');
      }
      const { token: t, user: u, tenantId } = json.data || {};
      setUser(u);
      setToken(t);
      localStorage.setItem(STORAGE_USER, JSON.stringify(u));
      if (t) localStorage.setItem(STORAGE_TOKEN, t);
      if (tenantId) {
        // Si el backend devolvió tenantId, lo resolvemos vía API.
        try {
          const tr = await fetch(`${API_BASE_URL}/tenants/${tenantId}`, {
            headers: withTenant(),
          });
          const tj = await tr.json();
          const data = tj?.data || tj;
          if (data && data._id) {
            setTenant(data);
            localStorage.setItem(STORAGE_TENANT, JSON.stringify(data));
            // Guardamos también el slug en una key dedicada
            // para que `getTenantSlug()` lo encuentre sin tener
            // que parsear el JSON del tenant completo.
            if (data.slug) setTenantSlug(data.slug);
          }
        } catch {
          // No bloqueamos login si falla el fetch de tenant.
        }
      } else {
        setTenant(null);
        localStorage.removeItem(STORAGE_TENANT);
        setTenantSlug(null);
      }
      return u;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      tenant,
      token,
      loading,
      slug,
      isPlatformMode,
      isTenantMode,
      isAdmin: user?.rol === 'admin',
      isClient: user?.rol === 'cliente',
      login,
      logout,
    }),
    [user, tenant, token, loading, slug, isPlatformMode, isTenantMode, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
